import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { drag, type D3DragEvent } from "d3-drag";
import {
  forceCenter,
  forceCollide,
  forceLink,
  forceManyBody,
  forceRadial,
  forceSimulation,
  type ForceCollide,
  type Simulation,
  type SimulationLinkDatum,
  type SimulationNodeDatum,
} from "d3-force";
import { select } from "d3-selection";
import {
  zoom,
  zoomIdentity,
  zoomTransform,
  type ZoomBehavior,
  type ZoomTransform,
} from "d3-zoom";
import {
  Copy,
  Crosshair,
  FileText,
  Maximize2,
  Minus,
  Plus,
  Route,
  Search,
  Target,
  X,
} from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import {
  decisions as decisionsApi,
  entities as entitiesApi,
  relationships as relApi,
} from "@/lib/api";
import { colorForType } from "@/lib/entity-color";
import { useFocusSet } from "@/lib/use-focus";
import { DomainAwareEmpty } from "@/components/canvas/empty-hint";

interface Node extends SimulationNodeDatum {
  id: string;
  name: string;
  type: string;
  color: string;
}

interface Link extends SimulationLinkDatum<Node> {
  id: string;
  type: string;
}

type LayoutMode = "force" | "radial";

// ── Card geometry ────────────────────────────────────────────────────────
// Nodes render as labeled "chips" (icon dot + name), not bare circles — the
// concept-map look. Width is estimated from the (truncated) label since we
// can't measure SVG text without a DOM round-trip; the estimate only needs to
// be good enough to size the collision radius and the chip background.
const CARD_H = 30;
const CARD_PAD_X = 10;
const CARD_ICON = 16;
const CHAR_PX = 6.6; // rough advance width at our 12px label size
const NAME_MAX = 22; // truncate longer names with an ellipsis

function cardLabel(name: string): string {
  return name.length > NAME_MAX ? `${name.slice(0, NAME_MAX - 1)}…` : name;
}
function cardWidth(name: string): number {
  const label = cardLabel(name);
  return Math.round(CARD_PAD_X * 2 + CARD_ICON + 6 + label.length * CHAR_PX);
}

export function LiveNetworkCanvas({ workspaceId }: { workspaceId: string }) {
  const ents = useQuery({
    queryKey: ["entities", workspaceId],
    queryFn: () => entitiesApi.list(workspaceId),
    enabled: Boolean(workspaceId),
  });
  const rels = useQuery({
    queryKey: ["relationships", workspaceId],
    queryFn: () => relApi.list(workspaceId),
    enabled: Boolean(workspaceId),
  });

  const [hiddenTypes, setHiddenTypes] = useState<Set<string>>(new Set());
  const toggleType = (t: string) => {
    setHiddenTypes((prev) => {
      const next = new Set(prev);
      next.has(t) ? next.delete(t) : next.add(t);
      return next;
    });
  };

  const [layoutMode, setLayoutMode] = useState<LayoutMode>("force");

  // ── Focus (hybrid model) ──────────────────────────────────────────────
  // Whole graph by default. "Focus" on a node collapses the view to that
  // node and progressively-expandable neighborhood (Maltego style). The set
  // of *expanded* nodes seeds which neighbors are revealed: visible = every
  // expanded node plus the direct neighbors of each. `focusRoot === null`
  // means "show everything".
  const [focusRoot, setFocusRoot] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const enterFocus = (id: string) => {
    setFocusRoot(id);
    setExpanded(new Set([id]));
  };
  const exitFocus = () => {
    setFocusRoot(null);
    setExpanded(new Set());
  };
  const expand = (id: string) => setExpanded((p) => new Set(p).add(id));

  // Shortest-path mode (kept from prior version).
  const [pathMode, setPathMode] = useState(false);
  const [pathStart, setPathStart] = useState<string | null>(null);
  const [pathEnd, setPathEnd] = useState<string | null>(null);
  const clearPath = () => {
    setPathMode(false);
    setPathStart(null);
    setPathEnd(null);
  };

  const allNodes = useMemo<Node[]>(
    () =>
      (ents.data ?? []).map((e) => ({
        id: e.id,
        name: e.name,
        type: e.type,
        color: e.color ?? colorForType(e.type),
      })),
    [ents.data],
  );
  const typeCounts = useMemo(() => {
    const m: Record<string, number> = {};
    for (const n of allNodes) m[n.type] = (m[n.type] ?? 0) + 1;
    return m;
  }, [allNodes]);

  // Adjacency over the *type-filtered* graph, used both for path BFS and for
  // focus-mode neighbor expansion.
  const typeVisibleNodes = useMemo(
    () => allNodes.filter((n) => !hiddenTypes.has(n.type)),
    [allNodes, hiddenTypes],
  );
  const typeVisibleIds = useMemo(
    () => new Set(typeVisibleNodes.map((n) => n.id)),
    [typeVisibleNodes],
  );
  const allLinks = useMemo(
    () =>
      (rels.data ?? [])
        .filter(
          (r) =>
            typeVisibleIds.has(r.source_entity_id) &&
            typeVisibleIds.has(r.target_entity_id),
        )
        .map((r) => ({
          id: r.id,
          source: r.source_entity_id,
          target: r.target_entity_id,
          type: r.type,
        })),
    [rels.data, typeVisibleIds],
  );
  const adjacency = useMemo(() => {
    const m = new Map<string, Set<string>>();
    for (const l of allLinks) {
      const a = String(l.source);
      const b = String(l.target);
      (m.get(a) ?? m.set(a, new Set()).get(a)!).add(b);
      (m.get(b) ?? m.set(b, new Set()).get(b)!).add(a);
    }
    return m;
  }, [allLinks]);

  // Visible node set depends on focus. In focus mode we take the union of the
  // expanded nodes and all their direct neighbors.
  const visibleIds = useMemo(() => {
    if (!focusRoot) return typeVisibleIds;
    const out = new Set<string>();
    for (const id of expanded) {
      if (!typeVisibleIds.has(id)) continue;
      out.add(id);
      for (const nb of adjacency.get(id) ?? []) {
        if (typeVisibleIds.has(nb)) out.add(nb);
      }
    }
    // Root always present even if it was type-filtered out from under us.
    out.add(focusRoot);
    return out;
  }, [focusRoot, expanded, adjacency, typeVisibleIds]);

  const nodes = useMemo<Node[]>(
    () => typeVisibleNodes.filter((n) => visibleIds.has(n.id)),
    [typeVisibleNodes, visibleIds],
  );
  const links = useMemo<Link[]>(
    () =>
      allLinks.filter(
        (l) => visibleIds.has(String(l.source)) && visibleIds.has(String(l.target)),
      ),
    [allLinks, visibleIds],
  );

  // How many *more* neighbors a node would reveal if expanded — drives the
  // "+N" affordance on cards in focus mode.
  const hiddenNeighborCount = useMemo(() => {
    const m = new Map<string, number>();
    if (!focusRoot) return m;
    for (const n of nodes) {
      let hidden = 0;
      for (const nb of adjacency.get(n.id) ?? []) {
        if (typeVisibleIds.has(nb) && !visibleIds.has(nb)) hidden++;
      }
      if (hidden > 0) m.set(n.id, hidden);
    }
    return m;
  }, [focusRoot, nodes, adjacency, typeVisibleIds, visibleIds]);

  // BFS shortest path over the currently visible graph.
  const path = useMemo(() => {
    if (!pathStart || !pathEnd || pathStart === pathEnd)
      return { nodeIds: new Set<string>(), edgeKeys: new Set<string>() };
    const adj = new Map<string, { neighbor: string; edge: string }[]>();
    for (const l of links) {
      const a = String(l.source);
      const b = String(l.target);
      if (!adj.has(a)) adj.set(a, []);
      if (!adj.has(b)) adj.set(b, []);
      adj.get(a)!.push({ neighbor: b, edge: `${a}->${b}` });
      adj.get(b)!.push({ neighbor: a, edge: `${a}->${b}` });
    }
    const prev = new Map<string, { from: string; edge: string } | null>();
    prev.set(pathStart, null);
    const q = [pathStart];
    while (q.length) {
      const cur = q.shift()!;
      if (cur === pathEnd) break;
      for (const { neighbor, edge } of adj.get(cur) ?? []) {
        if (prev.has(neighbor)) continue;
        prev.set(neighbor, { from: cur, edge });
        q.push(neighbor);
      }
    }
    if (!prev.has(pathEnd))
      return { nodeIds: new Set<string>(), edgeKeys: new Set<string>() };
    const nodeIds = new Set<string>();
    const edgeKeys = new Set<string>();
    let cur: string | null = pathEnd;
    while (cur !== null) {
      nodeIds.add(cur);
      const step = prev.get(cur);
      if (!step) break;
      edgeKeys.add(step.edge);
      cur = step.from;
    }
    return { nodeIds, edgeKeys };
  }, [pathStart, pathEnd, links]);
  const pathActive = path.nodeIds.size > 0;

  const rootName = focusRoot
    ? allNodes.find((n) => n.id === focusRoot)?.name ?? focusRoot.slice(0, 8)
    : null;

  return (
    <div className="relative h-full w-full overflow-hidden rounded-md bg-[oklch(0.14_0.01_260)] flex flex-col">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-surface/60 text-[11px]">
        <div className="flex items-center gap-4 text-foreground/80">
          <span>
            <span className="font-mono text-foreground">{nodes.length}</span>{" "}
            <span className="text-muted-foreground">
              {focusRoot ? `of ${typeVisibleNodes.length} entities` : "entities"}
            </span>
          </span>
          <span>
            <span className="font-mono text-foreground">{links.length}</span>{" "}
            <span className="text-muted-foreground">links</span>
          </span>
          {focusRoot ? (
            <span className="inline-flex items-center gap-1.5 rounded bg-primary/15 px-2 py-0.5 text-primary">
              <Target className="h-3 w-3" />
              <span className="max-w-[160px] truncate">{rootName}</span>
              <button onClick={exitFocus} title="Exit focus" className="hover:text-foreground">
                <X className="h-3 w-3" />
              </button>
            </span>
          ) : (
            <span className="text-muted-foreground hidden md:inline">
              right-click a node for actions · scroll to zoom · drag to pan
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 text-muted-foreground">
            <span className="text-[10px] uppercase tracking-wider">Layout</span>
            <button
              onClick={() => setLayoutMode("force")}
              className={`rounded px-1.5 py-0.5 text-[11px] ${layoutMode === "force" ? "bg-primary/20 text-primary" : "hover:text-foreground"}`}
            >
              force
            </button>
            <button
              onClick={() => setLayoutMode("radial")}
              className={`rounded px-1.5 py-0.5 text-[11px] ${layoutMode === "radial" ? "bg-primary/20 text-primary" : "hover:text-foreground"}`}
            >
              radial
            </button>
          </div>
          <button
            onClick={() => (pathMode ? clearPath() : setPathMode(true))}
            className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-medium transition ${
              pathMode || pathActive
                ? "bg-primary/20 text-primary"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Route className="h-3 w-3" />
            {pathMode || pathActive ? "Cancel path" : "Find path"}
          </button>
          {(ents.isLoading || rels.isLoading) && (
            <span className="text-muted-foreground">Loading…</span>
          )}
        </div>
      </div>

      {(pathMode || pathActive) && (
        <div className="absolute left-1/2 top-12 -translate-x-1/2 z-30 inline-flex items-center gap-2 rounded-md border border-primary/30 bg-surface/95 backdrop-blur px-3 py-1.5 text-[11px] shadow-lg">
          {!pathStart && <span className="text-foreground/85">Click the start node…</span>}
          {pathStart && !pathEnd && (
            <span className="text-foreground/85">
              Start:{" "}
              <span className="font-mono text-primary">
                {allNodes.find((n) => n.id === pathStart)?.name ?? pathStart.slice(0, 6)}
              </span>{" "}
              · click the end node
            </span>
          )}
          {pathActive && (
            <span className="text-foreground/85">
              {path.nodeIds.size === 0
                ? "No path between those nodes"
                : `Path: ${path.nodeIds.size} nodes · ${path.edgeKeys.size} edges`}
            </span>
          )}
          <button onClick={clearPath} className="text-muted-foreground hover:text-foreground">
            close
          </button>
        </div>
      )}

      <div className="relative flex-1 min-h-0">
        {nodes.length === 0 && !ents.isLoading ? (
          <DomainAwareEmpty workspaceId={workspaceId} kind="entity" />
        ) : (
          <ConceptGraph
            workspaceId={workspaceId}
            nodes={nodes}
            links={links}
            layoutMode={layoutMode}
            focusRoot={focusRoot}
            hiddenNeighborCount={hiddenNeighborCount}
            pathMode={pathMode}
            pathNodeIds={path.nodeIds}
            pathEdgeKeys={path.edgeKeys}
            pathStart={pathStart}
            pathEnd={pathEnd}
            onNodePathClick={(id) => {
              // Also arm path mode. The context menu's "Use in path finder"
              // routes here without going through the toolbar, and without
              // this the banner never renders — picking a start node from
              // the menu looked like it did nothing.
              setPathMode(true);
              if (!pathStart) setPathStart(id);
              else if (!pathEnd) setPathEnd(id);
            }}
            onFocus={enterFocus}
            onExpand={expand}
          />
        )}
        <TypeFilterPanel
          types={Array.from(new Set(allNodes.map((n) => n.type))).sort()}
          hiddenTypes={hiddenTypes}
          onToggle={toggleType}
          onSetAll={(hidden) =>
            setHiddenTypes(hidden ? new Set(allNodes.map((n) => n.type)) : new Set())
          }
          counts={typeCounts}
        />
      </div>
    </div>
  );
}

function ConceptGraph({
  workspaceId,
  nodes,
  links,
  layoutMode,
  focusRoot,
  hiddenNeighborCount,
  pathMode,
  pathNodeIds,
  pathEdgeKeys,
  pathStart,
  pathEnd,
  onNodePathClick,
  onFocus,
  onExpand,
}: {
  workspaceId: string;
  nodes: Node[];
  links: Link[];
  layoutMode: LayoutMode;
  focusRoot: string | null;
  hiddenNeighborCount: Map<string, number>;
  pathMode: boolean;
  pathNodeIds: Set<string>;
  pathEdgeKeys: Set<string>;
  pathStart: string | null;
  pathEnd: string | null;
  onNodePathClick: (id: string) => void;
  onFocus: (id: string) => void;
  onExpand: (id: string) => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const viewportRef = useRef<SVGGElement | null>(null);
  const zoomBehaviorRef = useRef<ZoomBehavior<SVGSVGElement, unknown> | null>(null);
  const [size, setSize] = useState({ w: 800, h: 600 });
  const [transform, setTransform] = useState<ZoomTransform>(zoomIdentity);
  const simRef = useRef<Simulation<Node, Link> | null>(null);
  const [hover, setHover] = useState<string | null>(null);
  const [, forceRerender] = useState(0);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const focusSet = useFocusSet();

  // Cursor-anchored context menu (custom — the nodes are SVG, so a radix
  // Trigger-per-node would be awkward; a positioned overlay is simpler).
  const [menu, setMenu] = useState<{ x: number; y: number; node: Node } | null>(null);
  const closeMenu = () => setMenu(null);

  const pathActive = pathNodeIds.size > 0;
  const isDimmed = (id: string) => {
    if (pathActive) return !pathNodeIds.has(id);
    return focusSet.size > 0 && !focusSet.has(id);
  };
  const isPathEdge = (l: Link) => {
    if (pathEdgeKeys.size === 0) return false;
    const a = typeof l.source === "string" ? l.source : (l.source as Node).id;
    const b = typeof l.target === "string" ? l.target : (l.target as Node).id;
    return pathEdgeKeys.has(`${a}->${b}`) || pathEdgeKeys.has(`${b}->${a}`);
  };
  const openEntity = (id: string) =>
    navigate({ to: ".", search: (prev: Record<string, unknown>) => ({ ...prev, entity: id }) });

  const generateReport = useMutation({
    mutationFn: (node: Node) => {
      const decision_input = [
        `Assess the operational risk associated with **${node.name}** (${node.type}).`,
        "",
        "Use its relationships in the graph as context. Return a risk score",
        "(low/medium/high), the most likely scenarios, and 2–3 concrete mitigations.",
      ].join("\n");
      return decisionsApi.create(workspaceId, {
        title: `Risk assessment: ${node.name}`,
        decision_input,
      });
    },
    onSuccess: (decision) => {
      queryClient.invalidateQueries({ queryKey: ["decisions", workspaceId] });
      navigate({
        to: "/$workspace/decisions/$decisionId",
        params: { workspace: workspaceId, decisionId: decision.id },
      });
    },
  });

  // Track container size.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize({ w: Math.max(200, width), h: Math.max(200, height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Simulation. Collide radius is sized to the *card* (half-width + margin) so
  // labeled chips don't overlap — the core readability fix.
  useEffect(() => {
    simRef.current?.stop();
    if (nodes.length === 0) return;
    const linkDistance = Math.max(140, Math.min(260, 110 + nodes.length * 0.4));
    const charge = layoutMode === "radial" ? -600 : -900;
    const sim = forceSimulation<Node, Link>(nodes)
      .force("charge", forceManyBody<Node>().strength(charge))
      .force(
        "link",
        forceLink<Node, Link>(links).id((d) => d.id).distance(linkDistance).strength(0.4),
      )
      .force(
        "collide",
        forceCollide<Node>()
          .radius((n) => cardWidth(n.name) / 2 + 16)
          .strength(0.9),
      )
      .force("center", forceCenter(size.w / 2, size.h / 2))
      .alphaDecay(0.045)
      .on("tick", () => forceRerender((t) => t + 1));

    if (layoutMode === "radial") {
      const R = Math.max(200, Math.min(size.w, size.h) * 0.4);
      const degree = new Map<string, number>();
      for (const l of links) {
        const a = typeof l.source === "string" ? l.source : (l.source as Node).id;
        const b = typeof l.target === "string" ? l.target : (l.target as Node).id;
        degree.set(a, (degree.get(a) ?? 0) + 1);
        degree.set(b, (degree.get(b) ?? 0) + 1);
      }
      sim.force(
        "radial",
        forceRadial<Node>(
          (n) => (n.id === focusRoot ? 0 : (degree.get(n.id) ?? 0) >= 4 ? R * 0.2 : R),
          size.w / 2,
          size.h / 2,
        ).strength(0.4),
      );
    }
    simRef.current = sim;
    return () => {
      sim.stop();
    };
  }, [nodes, links, size.w, size.h, layoutMode, focusRoot]);

  // Zoom + pan.
  useEffect(() => {
    if (!svgRef.current) return;
    const svg = select<SVGSVGElement, unknown>(svgRef.current);
    const z = zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 4])
      .filter((event) => {
        const target = event.target as Element | null;
        if (target?.closest("[data-node]")) return false;
        return !event.ctrlKey && event.type !== "dblclick";
      })
      .on("zoom", (event) => setTransform(event.transform));
    svg.call(z);
    zoomBehaviorRef.current = z;
    return () => {
      svg.on(".zoom", null);
      zoomBehaviorRef.current = null;
    };
  }, []);

  // Node drag.
  useEffect(() => {
    if (!viewportRef.current) return;
    const sim = simRef.current;
    if (!sim) return;
    const nodesSel = select(viewportRef.current).selectAll<SVGGElement, Node>("[data-node]");
    nodesSel.call(
      drag<SVGGElement, Node>()
        .on("start", (event: D3DragEvent<SVGGElement, Node, Node>, d) => {
          if (!event.active) sim.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on("drag", (event: D3DragEvent<SVGGElement, Node, Node>, d) => {
          const t = zoomTransform(svgRef.current as SVGSVGElement);
          const k = t.k || 1;
          d.fx = (d.fx ?? 0) + event.dx / k;
          d.fy = (d.fy ?? 0) + event.dy / k;
        })
        .on("end", (event: D3DragEvent<SVGGElement, Node, Node>, d) => {
          if (!event.active) sim.alphaTarget(0);
          d.fx = null;
          d.fy = null;
        }),
    );
  }, [nodes]);

  // Close the context menu on any outside interaction / escape.
  useEffect(() => {
    if (!menu) return;
    const onDown = () => closeMenu();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && closeMenu();
    const t = window.setTimeout(() => window.addEventListener("mousedown", onDown), 0);
    window.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [menu]);

  const zoomBy = (factor: number) => {
    const svgEl = svgRef.current;
    const z = zoomBehaviorRef.current;
    if (!svgEl || !z) return;
    z.scaleBy(select<SVGSVGElement, unknown>(svgEl), factor);
  };
  const resetZoom = () => {
    const svgEl = svgRef.current;
    const z = zoomBehaviorRef.current;
    if (!svgEl || !z) return;
    z.transform(select<SVGSVGElement, unknown>(svgEl), zoomIdentity);
  };

  return (
    <div ref={containerRef} className="absolute inset-0">
      <svg
        ref={svgRef}
        width={size.w}
        height={size.h}
        className="absolute inset-0 cursor-grab active:cursor-grabbing"
        onContextMenu={(e) => e.preventDefault()}
      >
        <defs>
          <marker
            id="net-arrow"
            viewBox="0 -5 10 10"
            refX="8"
            refY="0"
            markerWidth="6"
            markerHeight="6"
            orient="auto"
          >
            <path d="M0,-5L10,0L0,5" fill="oklch(0.5 0.015 260)" />
          </marker>
          {/* Subtle dot grid, like the reference. */}
          <pattern id="net-grid" width="26" height="26" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="1" fill="oklch(0.28 0.01 260)" />
          </pattern>
        </defs>
        <rect x={0} y={0} width={size.w} height={size.h} fill="url(#net-grid)" opacity={0.5} />

        <g ref={viewportRef} transform={transform.toString()}>
          {/* Edges */}
          {links.map((l) => {
            const s = typeof l.source === "object" ? (l.source as Node) : undefined;
            const t = typeof l.target === "object" ? (l.target as Node) : undefined;
            if (!s || !t || s.x == null || s.y == null || t.x == null || t.y == null) return null;
            const dim = isDimmed(s.id) && isDimmed(t.id);
            const inPath = isPathEdge(l);
            const touchesHover = hover === s.id || hover === t.id;
            const showLabel = inPath || touchesHover || transform.k >= 1.5;
            return (
              <g key={l.id} opacity={dim ? 0.12 : 1}>
                <line
                  x1={s.x}
                  y1={s.y}
                  x2={t.x}
                  y2={t.y}
                  stroke={inPath ? "var(--color-primary)" : "oklch(0.42 0.015 260)"}
                  strokeWidth={(inPath ? 2.5 : 1) / Math.max(0.5, transform.k)}
                  strokeOpacity={inPath ? 1 : 0.7}
                  markerEnd="url(#net-arrow)"
                />
                {showLabel && (
                  <text
                    x={(s.x + t.x) / 2}
                    y={(s.y + t.y) / 2 - 3}
                    fontSize={9}
                    fill="oklch(0.72 0.01 250)"
                    textAnchor="middle"
                    pointerEvents="none"
                  >
                    {l.type}
                  </text>
                )}
              </g>
            );
          })}

          {/* Nodes as cards */}
          {nodes.map((n) => {
            if (n.x == null || n.y == null) return null;
            const w = cardWidth(n.name);
            const isHover = hover === n.id;
            const dim = isDimmed(n.id);
            const isRoot = focusRoot === n.id;
            const isEndpoint = pathStart === n.id || pathEnd === n.id;
            const hiddenN = hiddenNeighborCount.get(n.id) ?? 0;
            const accent =
              isEndpoint || isRoot ? "var(--color-primary)" : n.color;
            return (
              <g
                key={n.id}
                data-node
                transform={`translate(${n.x - w / 2}, ${n.y - CARD_H / 2})`}
                opacity={dim ? 0.25 : 1}
                onMouseEnter={() => setHover(n.id)}
                onMouseLeave={() => setHover(null)}
                onClick={() => {
                  if (pathMode) onNodePathClick(n.id);
                  else openEntity(n.id);
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const rect = containerRef.current?.getBoundingClientRect();
                  setMenu({
                    x: e.clientX - (rect?.left ?? 0),
                    y: e.clientY - (rect?.top ?? 0),
                    node: n,
                  });
                }}
                style={{ cursor: pathMode ? "crosshair" : "pointer" }}
              >
                <rect
                  width={w}
                  height={CARD_H}
                  rx={6}
                  fill="oklch(0.19 0.012 260)"
                  stroke={accent}
                  strokeWidth={isHover || isRoot || isEndpoint ? 2 : 1.25}
                  filter={
                    isHover || isRoot || isEndpoint
                      ? "drop-shadow(0 1px 6px rgba(0,0,0,0.5))"
                      : undefined
                  }
                />
                {/* Left accent stripe */}
                <rect width={4} height={CARD_H} rx={2} fill={accent} />
                {/* Icon dot */}
                <circle cx={CARD_PAD_X + 5} cy={CARD_H / 2} r={5} fill={n.color} />
                {/* Name */}
                <text
                  x={CARD_PAD_X + CARD_ICON}
                  y={CARD_H / 2 - 1}
                  dominantBaseline="middle"
                  fontSize={12}
                  fontWeight={500}
                  fill="oklch(0.95 0.005 260)"
                  pointerEvents="none"
                >
                  {cardLabel(n.name)}
                </text>
                {/* Type subtext */}
                <text
                  x={CARD_PAD_X + CARD_ICON}
                  y={CARD_H - 6}
                  fontSize={7.5}
                  fill="oklch(0.6 0.015 260)"
                  pointerEvents="none"
                >
                  {n.type.replace(/_/g, " ")}
                </text>
                {/* Expand affordance (+N) in focus mode */}
                {hiddenN > 0 && (
                  <g
                    onClick={(e) => {
                      e.stopPropagation();
                      onExpand(n.id);
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <circle cx={w} cy={CARD_H / 2} r={9} fill="var(--color-primary)" />
                    <text
                      x={w}
                      y={CARD_H / 2}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontSize={8.5}
                      fontWeight={700}
                      fill="#0a0a0c"
                    >
                      +{hiddenN > 99 ? "99" : hiddenN}
                    </text>
                  </g>
                )}
              </g>
            );
          })}
        </g>
      </svg>

      {/* Context menu */}
      {menu && (
        <NodeContextMenu
          x={menu.x}
          y={menu.y}
          node={menu.node}
          inFocus={Boolean(focusRoot)}
          isRoot={focusRoot === menu.node.id}
          reportPending={generateReport.isPending}
          onClose={closeMenu}
          onFocus={() => {
            onFocus(menu.node.id);
            closeMenu();
          }}
          onExpand={() => {
            onExpand(menu.node.id);
            closeMenu();
          }}
          onOpenDetails={() => {
            openEntity(menu.node.id);
            closeMenu();
          }}
          onFindPath={() => {
            onNodePathClick(menu.node.id);
            closeMenu();
          }}
          onReport={() => {
            generateReport.mutate(menu.node);
            closeMenu();
          }}
          onCopy={() => {
            navigator.clipboard?.writeText(menu.node.name).catch(() => {});
            closeMenu();
          }}
        />
      )}

      {/* Zoom controls */}
      <div className="absolute right-3 bottom-3 z-20 flex flex-col gap-1">
        <button
          onClick={() => zoomBy(1.4)}
          className="grid h-8 w-8 place-items-center rounded-md border border-border bg-surface text-foreground/80 hover:bg-surface-elevated"
          title="Zoom in"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={() => zoomBy(1 / 1.4)}
          className="grid h-8 w-8 place-items-center rounded-md border border-border bg-surface text-foreground/80 hover:bg-surface-elevated"
          title="Zoom out"
        >
          <Minus className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={resetZoom}
          className="grid h-8 w-8 place-items-center rounded-md border border-border bg-surface text-foreground/80 hover:bg-surface-elevated"
          title="Reset zoom"
        >
          <Maximize2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function NodeContextMenu({
  x,
  y,
  node,
  inFocus,
  isRoot,
  reportPending,
  onClose,
  onFocus,
  onExpand,
  onOpenDetails,
  onFindPath,
  onReport,
  onCopy,
}: {
  x: number;
  y: number;
  node: Node;
  inFocus: boolean;
  isRoot: boolean;
  reportPending: boolean;
  onClose: () => void;
  onFocus: () => void;
  onExpand: () => void;
  onOpenDetails: () => void;
  onFindPath: () => void;
  onReport: () => void;
  onCopy: () => void;
}) {
  // Clamp inside the graph container (not the window). x/y are already in
  // container space, so measuring against innerWidth would let the menu run
  // past the canvas edge whenever the sidebar/chat panel makes the container
  // narrower than the viewport — which is always. Measured before paint so
  // the first frame is already in the right place.
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    const container = el?.parentElement;
    if (!el || !container) return;
    const w = el.offsetWidth || 210;
    const h = el.offsetHeight || 200;
    let left = x;
    let top = y;
    if (left + w > container.clientWidth - 8) left = x - w;
    if (top + h > container.clientHeight - 8) top = container.clientHeight - h - 8;
    setPos({ left: Math.max(8, left), top: Math.max(8, top) });
  }, [x, y]);

  const Item = ({
    icon: Icon,
    label,
    onClick,
    disabled,
    accent,
  }: {
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    onClick: () => void;
    disabled?: boolean;
    accent?: boolean;
  }) => (
    <button
      onMouseDown={(e) => e.stopPropagation()}
      onClick={onClick}
      disabled={disabled}
      className={`flex w-full items-center gap-2 px-3 py-1.5 text-[12px] text-left hover:bg-surface-elevated disabled:opacity-40 ${
        accent ? "text-primary" : "text-foreground/90"
      }`}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" />
      <span className="truncate">{label}</span>
    </button>
  );
  return (
    <div
      ref={ref}
      className="absolute z-40 w-[210px] overflow-hidden rounded-md border border-border bg-popover shadow-xl py-1"
      style={{
        left: `${pos?.left ?? x}px`,
        top: `${pos?.top ?? y}px`,
        visibility: pos ? "visible" : "hidden",
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="px-3 py-1.5 border-b border-border/70">
        <div className="text-[12px] font-semibold text-foreground truncate">{node.name}</div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
          {node.type.replace(/_/g, " ")}
        </div>
      </div>
      {!isRoot && <Item icon={Target} label="Focus on this node" onClick={onFocus} accent />}
      {inFocus && <Item icon={Crosshair} label="Expand neighbors" onClick={onExpand} />}
      <Item icon={FileText} label="Open details" onClick={onOpenDetails} />
      <Item icon={Route} label="Use in path finder" onClick={onFindPath} />
      <Item
        icon={FileText}
        label={reportPending ? "Generating…" : "Generate risk report"}
        onClick={onReport}
        disabled={reportPending}
      />
      <div className="my-1 border-t border-border/70" />
      <Item icon={Copy} label="Copy name" onClick={onCopy} />
    </div>
  );
}

/**
 * TypeFilterPanel — contained, scrollable, searchable list of every entity
 * type. Fixed max-height so it never spills off the viewport (the old legend
 * grew unbounded and ran off the top of the screen on graphs with 30+ types).
 */
function TypeFilterPanel({
  types,
  hiddenTypes,
  onToggle,
  onSetAll,
  counts,
}: {
  types: string[];
  hiddenTypes: Set<string>;
  onToggle: (type: string) => void;
  onSetAll: (hidden: boolean) => void;
  counts: Record<string, number>;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [q, setQ] = useState("");
  if (types.length === 0) return null;
  const filtered = types.filter((t) => t.toLowerCase().includes(q.toLowerCase()));
  const shownCount = types.length - hiddenTypes.size;
  return (
    <div className="absolute left-3 top-3 z-20 w-[210px] rounded-md border border-border bg-surface/95 backdrop-blur text-[11px] flex flex-col max-h-[calc(100%-1.5rem)]">
      <button
        onClick={() => setCollapsed((c) => !c)}
        className="flex items-center justify-between px-3 py-2 border-b border-border text-foreground/85 hover:bg-surface-elevated shrink-0"
      >
        <span className="label-eyebrow !mb-0">
          Types · {shownCount}/{types.length}
        </span>
        <span className="text-muted-foreground">{collapsed ? "▸" : "▾"}</span>
      </button>
      {!collapsed && (
        <>
          <div className="flex items-center gap-1 px-2 py-1.5 border-b border-border/60 shrink-0">
            <Search className="h-3 w-3 text-muted-foreground shrink-0" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter types…"
              className="w-full bg-transparent text-[11px] text-foreground placeholder:text-muted-foreground focus:outline-none"
            />
            {q && (
              <button onClick={() => setQ("")} className="text-muted-foreground hover:text-foreground">
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 px-3 py-1 border-b border-border/60 text-[10px] text-muted-foreground shrink-0">
            <button onClick={() => onSetAll(false)} className="hover:text-foreground">
              Show all
            </button>
            <span>·</span>
            <button onClick={() => onSetAll(true)} className="hover:text-foreground">
              Hide all
            </button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-muted-foreground italic">No types match.</div>
            ) : (
              filtered.map((t) => {
                const hidden = hiddenTypes.has(t);
                return (
                  <button
                    key={t}
                    onClick={() => onToggle(t)}
                    className={`w-full flex items-center gap-2 px-3 py-1 hover:bg-surface-elevated transition ${
                      hidden ? "opacity-40" : ""
                    }`}
                  >
                    <span
                      className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
                      style={{ background: colorForType(t) }}
                    />
                    <span className="text-foreground/85 capitalize flex-1 text-left truncate">
                      {t.replace(/_/g, " ")}
                    </span>
                    <span className="font-mono text-[10px] text-muted-foreground shrink-0">
                      {counts[t] ?? 0}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
}
